import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { PaginationComponent } from '@components/pagination-component/pagination-component';

import { ModalService } from '@services/components/ModalService/modal.service';
import { ToastService } from '@services/components/ToastService/toast.service';
import {
  OrderStatus,
  OrderStatusCounts,
  OrderSummary,
  PaginatedOrders,
  ShippingType,
} from '@services/orders/interfaces/orders.interfaces';
import { OrdersService } from '@services/orders/orders.service';
import { GenericErrorModalComponent } from '@shared/components/UI/generic-error-modal/generic-error-modal.component';
import { OrderStatusBadgeComponent } from '@shared/components/UI/order-status-badge/order-status-badge.component';
import { ClpCurrencyPipe } from '@shared/pipes/clp-currency.pipe';

import {
  CancelOrderModalComponent,
  CancelOrderModalData,
} from './cancel-order-modal/cancel-order-modal.component';
import {
  OrderDetailModalComponent,
  OrderDetailModalData,
} from './order-detail-modal/order-detail-modal.component';
import {
  ReturnOrderModalComponent,
  ReturnOrderModalData,
} from './return-order-modal/return-order-modal.component';
import {
  ShipOrderModalComponent,
  ShipOrderModalData,
} from './ship-order-modal/ship-order-modal.component';

import {
  CANCEL_ORDER_MODAL_CONFIG,
  LOAD_ERROR_MODAL_CONFIG,
  ORDER_DETAIL_MODAL_CONFIG,
  RETURN_ORDER_MODAL_CONFIG,
  SHIP_ORDER_MODAL_CONFIG,
} from '@constants/modal.constants';
import {
  isFinalStatus as isFinalOrderStatus,
  ORDER_DATE_FORMAT,
  ORDER_STATUS_FILTERS,
  OrderStatusFilter,
  OrderStatusFilterOption,
  selectableStatusesFor,
  SHIPPING_TYPE_LABELS,
  statusLabelFor,
  UNRESOLVED_PAYMENT_LABELS,
} from '@constants/orders.constants';
import {
  buildOrderStatusErrorToast,
  buildOrderStatusToast,
  ERROR_ORDER_STATUS_TOAST,
  ORDER_NUMBER_COPIED_TOAST,
} from '@constants/toasts.constants';

/** Pedidos por página en el historial del admin. */
const PAGE_LIMIT = 10;

const SEARCH_DEBOUNCE_MS = 400;

const EMPTY_COUNTS: OrderStatusCounts = {
  all: 0,
  byStatus: { pending: 0, shipped: 0, completed: 0, cancelled: 0, returned: 0, payment_failed: 0 },
  pendingByShippingType: { delivery: 0, pickup: 0 },
  unconfirmedPayments: 0,
};

@Component({
  selector: 'app-orders-history',
  standalone: true,
  imports: [CommonModule, OrderStatusBadgeComponent, ClpCurrencyPipe, PaginationComponent],
  templateUrl: './orders-history.html',
  styleUrl: './orders-history.css',
})
export class OrdersHistory implements OnInit {
  public readonly orders = signal<OrderSummary[]>([]);
  public readonly isLoading = signal(false);
  public readonly searchTerm = signal('');
  public readonly activeFilter = signal<OrderStatusFilter>('ALL');
  public readonly currentPage = signal(1);
  public readonly totalPages = signal(1);
  /** Totales por estado del listado completo, no solo de la página visible. */
  public readonly statusCounts = signal<OrderStatusCounts>(EMPTY_COUNTS);
  /** Ids of orders with an in-flight status update, used to disable their dropdown. */
  public readonly updatingIds = signal<ReadonlySet<string>>(new Set<string>());
  /** Id of the order whose detail is being fetched before opening the modal. */
  public readonly detailLoadingId = signal<string | null>(null);

  public readonly statusFilters = ORDER_STATUS_FILTERS;
  public readonly dateFormat = ORDER_DATE_FORMAT;

  private readonly ordersService = inject(OrdersService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(ModalService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private readonly searchSubject = new BehaviorSubject<string>('');
  private readonly filterSubject = new BehaviorSubject<OrderStatusFilter>('ALL');
  private readonly pageSubject = new BehaviorSubject<number>(1);

  /**
   * Vuelve a pedir la página actual sin cambiar búsqueda, filtro ni número de
   * página. Es un stream aparte porque los otros tres llevan
   * `distinctUntilChanged`: emitir el mismo número de página no dispara nada, y
   * por eso el botón de reintentar y el refresco tras cambiar un estado no
   * hacían nada.
   */
  private readonly refreshSubject = new BehaviorSubject<void>(undefined);

  ngOnInit(): void {
    // startWith antes de distinctUntilChanged: la primera carga es inmediata y el
    // debounce solo retrasa lo que el usuario escribe después.
    const search$ = this.searchSubject.pipe(
      debounceTime(SEARCH_DEBOUNCE_MS),
      map((term) => term.trim()),
      startWith(''),
      distinctUntilChanged(),
    );

    // `onSearch` reinicia la página en cada tecla. Sin `distinctUntilChanged` ese
    // 1 → 1 hace emitir a `combineLatest` y se pierde el efecto del debounce:
    // salía una consulta por tecla con el término anterior.
    const filter$ = this.filterSubject.pipe(distinctUntilChanged());
    const page$ = this.pageSubject.pipe(distinctUntilChanged());

    // La página se pide al back: buscar o cambiar de pastilla vuelve a la página 1.
    combineLatest([search$, filter$, page$, this.refreshSubject])
      .pipe(
        switchMap(([search, filter, page]) => this.fetchOrders(search, filter, page)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => this.applyOrders(response));

    // Los contadores son del total, así que solo dependen de la búsqueda.
    search$
      .pipe(
        switchMap((search) =>
          this.ordersService
            .getOrderStatusCounts({ search: search || undefined })
            .pipe(catchError(() => of(EMPTY_COUNTS))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((counts) => this.statusCounts.set(counts));
  }

  public countFor(filter: OrderStatusFilter): number {
    const counts = this.statusCounts();

    if (filter === 'ALL') {
      return counts.all;
    }

    if (filter === 'payment-unconfirmed') {
      return counts.unconfirmedPayments;
    }

    // Las dos pastillas de pendientes salen del desglose por tipo de envío.
    if (filter === 'pending-delivery' || filter === 'pending-pickup') {
      return counts.pendingByShippingType[filter === 'pending-delivery' ? 'delivery' : 'pickup'];
    }

    return counts.byStatus[filter];
  }

  public onFilterChange(value: string): void {
    const option = this.statusFilters.find((filterOption) => filterOption.value === value);
    if (option) {
      this.setFilter(option.value);
    }
  }

  public setFilter(filter: OrderStatusFilter): void {
    this.activeFilter.set(filter);
    this.currentPage.set(1);
    this.pageSubject.next(1);
    this.filterSubject.next(filter);
  }

  public onSearch(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.searchTerm.set(term);
    this.currentPage.set(1);
    this.pageSubject.next(1);
    this.searchSubject.next(term);
  }

  public onPageChange(page: number): void {
    this.pageSubject.next(page);
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  public statusLabel(status: OrderStatus, shippingType: ShippingType): string {
    return statusLabelFor(status, shippingType);
  }

  /** Estado actual del pedido más los estados a los que puede pasar; son las únicas opciones válidas del dropdown. */
  public selectableStatuses(status: OrderStatus, shippingType: ShippingType): OrderStatus[] {
    return selectableStatusesFor(status, shippingType);
  }

  /** true si el select ni debe poder abrirse: sin transiciones posibles (cancelado, devuelto, o retiro que ya no pasa por "enviado"). */
  public isFinalStatus(status: OrderStatus, shippingType: ShippingType): boolean {
    return isFinalOrderStatus(status, shippingType);
  }

  public shippingLabel(order: OrderSummary): string {
    return SHIPPING_TYPE_LABELS[order.shippingType];
  }

  /**
   * Unresolved payment: the customer is still paying or TUU has not confirmed it yet.
   * Shipping it could mean sending something that was never paid.
   */
  public isPaymentPending(order: OrderSummary): boolean {
    return order.paymentStatus !== 'paid';
  }

  public paymentPillLabel(order: OrderSummary): string {
    return order.paymentStatus === 'paid' ? '' : UNRESOLVED_PAYMENT_LABELS[order.paymentStatus];
  }

  public statusCellHint(order: OrderSummary): string | null {
    if (order.paymentStatus === 'unconfirmed') {
      return 'TUU no confirmó el pago: revísalo en el Espacio de Trabajo de TUU antes de despachar';
    }

    if (this.isPaymentPending(order)) {
      return 'El pago aún no se confirma: no despaches este pedido';
    }

    return this.isFinalStatus(order.status, order.shippingType)
      ? 'Estado terminal, no se puede cambiar'
      : null;
  }

  public isUpdating(orderId: string): boolean {
    return this.updatingIds().has(orderId);
  }

  public onStatusChange(order: OrderSummary, event: Event): void {
    const select = event.target as HTMLSelectElement;
    const nextStatus = select.value as OrderStatus;
    this.requestStatusChange(order, nextStatus, select);
  }

  public requestStatusChange(order: OrderSummary, nextStatus: OrderStatus, select?: HTMLSelectElement): void {
    const previousStatus = order.status;

    if (nextStatus === previousStatus) {
      return;
    }

    // Enviado exige el código de seguimiento de Chilexpress, cancelado exige el motivo y devuelto lo
    // pide opcional. Mientras el modal está abierto no se toca el estado; si se cancela hay que
    // devolver el <select> a mano (ver `resetSelect`).
    if (nextStatus === 'shipped') {
      this.openShipOrderModal(order, previousStatus, select);
      return;
    }

    if (nextStatus === 'cancelled') {
      this.openCancelOrderModal(order, previousStatus, select);
      return;
    }

    // Devuelto también pide motivo, pero acá es opcional: se puede confirmar sin uno.
    if (nextStatus === 'returned') {
      this.openReturnOrderModal(order, previousStatus, select);
      return;
    }

    this.applyStatusChange(order, previousStatus, nextStatus);
  }

  /** The listing row lacks customer and item detail, so fetch the full order first. */
  public openDetail(order: OrderSummary): void {
    if (this.detailLoadingId()) {
      return;
    }

    this.detailLoadingId.set(order.id);

    this.ordersService
      .getOrderById(order.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.detailLoadingId.set(null);
          this.modalService.openModal<OrderStatus, OrderDetailModalData>({
            ...ORDER_DETAIL_MODAL_CONFIG,
            component: OrderDetailModalComponent,
            data: { order: detail },
          }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ reason, data: nextStatus }) => {
            if (reason !== 'confirm' || !nextStatus || detail.paymentStatus !== 'paid' || this.isUpdating(detail.id)) {
              return;
            }
            if (!selectableStatusesFor(detail.status, detail.shippingType).includes(nextStatus)) {
              return;
            }
            this.requestStatusChange({
              ...order,
              status: detail.status,
              paymentStatus: detail.paymentStatus,
              shippingType: detail.shippingType,
            }, nextStatus);
          });
        },
        error: () => {
          this.detailLoadingId.set(null);
          this.modalService.openModal({
            ...LOAD_ERROR_MODAL_CONFIG,
            component: GenericErrorModalComponent,
          });
        },
      });
  }

  public isDetailLoading(orderId: string): boolean {
    return this.detailLoadingId() === orderId;
  }

  public copyOrderNumber(order: OrderSummary): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const clipboard = window.navigator?.clipboard;

    if (!clipboard) {
      this.toastService.open(ERROR_ORDER_STATUS_TOAST);
      return;
    }

    clipboard
      .writeText(order.orderCode)
      .then(() => this.toastService.open(ORDER_NUMBER_COPIED_TOAST))
      .catch(() => this.toastService.open(ERROR_ORDER_STATUS_TOAST));
  }

  private openShipOrderModal(
    order: OrderSummary,
    previousStatus: OrderStatus,
    select?: HTMLSelectElement,
  ): void {
    this.modalService
      .openModal<string, ShipOrderModalData>({
        ...SHIP_ORDER_MODAL_CONFIG,
        component: ShipOrderModalComponent,
        // Último punto antes de despachar: si el pago no está confirmado, el
        // modal lo avisa. El backend no bloquea esta transición.
        data: { orderCode: order.orderCode, isPaid: !this.isPaymentPending(order) },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ reason, data: trackingCode }) => {
        if (reason !== 'confirm' || !trackingCode) {
          this.resetSelect(select, previousStatus);
          return;
        }
        this.applyStatusChange(order, previousStatus, 'shipped', { trackingCode });
      });
  }

  private openCancelOrderModal(
    order: OrderSummary,
    previousStatus: OrderStatus,
    select?: HTMLSelectElement,
  ): void {
    this.modalService
      .openModal<string, CancelOrderModalData>({
        ...CANCEL_ORDER_MODAL_CONFIG,
        component: CancelOrderModalComponent,
        data: { orderCode: order.orderCode },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ reason, data: cancelReason }) => {
        if (reason !== 'confirm' || !cancelReason) {
          this.resetSelect(select, previousStatus);
          return;
        }
        this.applyStatusChange(order, previousStatus, 'cancelled', { reason: cancelReason });
      });
  }

  private openReturnOrderModal(
    order: OrderSummary,
    previousStatus: OrderStatus,
    select?: HTMLSelectElement,
  ): void {
    this.modalService
      .openModal<string, ReturnOrderModalData>({
        ...RETURN_ORDER_MODAL_CONFIG,
        component: ReturnOrderModalComponent,
        data: { orderCode: order.orderCode },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ reason, data: returnReason }) => {
        if (reason !== 'confirm') {
          this.resetSelect(select, previousStatus);
          return;
        }

        // `returnReason` puede venir undefined: el motivo no es obligatorio.
        this.applyStatusChange(order, previousStatus, 'returned', { reason: returnReason });
      });
  }

  /**
   * La app es zoneless y cancelar el modal no cambia ninguna señal, así que el
   * `<select>` se queda mostrando la opción elegida aunque el pedido siga en su
   * estado anterior. Hay que devolverlo a mano.
   */
  private resetSelect(select: HTMLSelectElement | undefined, status: OrderStatus): void {
    if (select) {
      select.value = status;
    }
  }

  private applyStatusChange(
    order: OrderSummary,
    previousStatus: OrderStatus,
    nextStatus: OrderStatus,
    extra?: { trackingCode?: string; reason?: string },
  ): void {
    // Optimistic: the badge recolours immediately and rolls back if the call fails.
    this.patchStatus(order.id, nextStatus);
    this.markUpdating(order.id, true);

    this.ordersService
      .updateOrderStatus(order.id, { status: nextStatus, ...extra })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.markUpdating(order.id, false);
          this.toastService.open(buildOrderStatusToast(statusLabelFor(nextStatus, order.shippingType)));
          this.refreshCounts();

          // Con una pastilla de estado activa, el pedido ya no pertenece a esta
          // lista: se vuelve a pedir la página para que salga de la tabla.
          if (this.activeFilter() !== 'ALL') {
            this.refreshSubject.next();
          }
        },
        error: (error: unknown) => {
          this.patchStatus(order.id, previousStatus);
          this.markUpdating(order.id, false);
          this.toastService.open(buildOrderStatusErrorToast(this.extractErrorMessage(error)));
        },
      });
  }

  /** El back manda el motivo real (ej. "Transición de estado inválida...") en el body del 400. */
  private extractErrorMessage(error: unknown): string | undefined {
    const message = (error as HttpErrorResponse | undefined)?.error?.message;
    return typeof message === 'string' ? message : undefined;
  }

  private patchStatus(orderId: string, status: OrderStatus): void {
    this.orders.update((orders) =>
      orders.map((order) => (order.id === orderId ? { ...order, status } : order)),
    );
  }

  private markUpdating(orderId: string, updating: boolean): void {
    this.updatingIds.update((ids) => {
      const next = new Set(ids);

      if (updating) {
        next.add(orderId);
      } else {
        next.delete(orderId);
      }

      return next;
    });
  }

  // El error se atrapa acá y no en el subscribe: si el observable muriera, el
  // botón de reintentar del modal no volvería a cargar nada.
  private fetchOrders(search: string, filter: OrderStatusFilter, page: number): Observable<PaginatedOrders> {
    this.isLoading.set(true);
    const option = this.optionFor(filter);

    return this.ordersService
      .getOrdersAdmin(page, PAGE_LIMIT, {
        search: search || undefined,
        status: option?.status,
        shippingType: option?.shippingType,
        paymentStatus: option?.paymentStatus,
      })
      .pipe(
        catchError(() => {
          this.openLoadErrorModal();
          return of<PaginatedOrders>({ data: [], total: 0, page, limit: PAGE_LIMIT, totalPages: 1 });
        }),
      );
  }

  private optionFor(filter: OrderStatusFilter): OrderStatusFilterOption | undefined {
    return ORDER_STATUS_FILTERS.find((option) => option.value === filter);
  }

  private applyOrders(response: PaginatedOrders): void {
    this.orders.set(response.data);
    this.currentPage.set(response.page);
    this.totalPages.set(response.totalPages || 1);
    this.isLoading.set(false);
  }

  private openLoadErrorModal(): void {
    this.modalService
      .openModal<void>({
        ...LOAD_ERROR_MODAL_CONFIG,
        component: GenericErrorModalComponent,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ reason }) => {
        if (reason === 'confirm') {
          this.reload();
        }
      });
  }

  /** Vuelve a pedir la página actual y los contadores. */
  private reload(): void {
    this.refreshSubject.next();
    this.refreshCounts();
  }

  private refreshCounts(): void {
    this.ordersService
      .getOrderStatusCounts({ search: this.searchTerm() || undefined })
      .pipe(
        catchError(() => of(EMPTY_COUNTS)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((counts) => this.statusCounts.set(counts));
  }
}
